import React, { useEffect, useMemo, useRef } from 'react';

import {
  N_OF_MEDIAN_TIME,
  INCLUDED_COUNTRIES_FOR_WORKSHIFT,
  EXCLUDED_DESIGNATION_FOR_WORKSHIFT,
} from 'constants/dashboard';
import { SELF } from 'constants/fetchTypes';

import { isObjectEmpty } from 'utils/object';
import { handleError } from 'utils/errorHandler';
import { convertTimeToAMPM, getMedianTime } from 'utils/time';
import { getWorkStatusSummaryData, getWorklogTotalDuration } from 'utils/leave';
import { subtractDays, getFormattedDate, getCurrentWeekRange, filterDataByDateRange } from 'utils/date';

import useCurrentFiscalYear from 'hooks/useCurrentFiscalYear';
import { useCurrentUser } from 'hooks/query/useUserInfo';
import { useSelfAttendance } from 'contexts/SelfAttendanceContext';
import { useFetchAttendanceCalendarQuery, useFetchUnfreezeRequestsQuery } from 'hooks/query/useAttendanceQueries';
import { useFetchAttendanceSummary } from 'hooks/query/useFetchAttendanceSummary';

import LoggingStatusCard from './components/LoggingStatusCard';
import WorkStatusCard from './components/WorkStatusCard';

import type { WorkStatusSummary } from 'interface/leave';
import type { DashboardUser, FiscalYear } from 'interface/dashboard';
import type { CalendarDay, UnfreezeRequest, UnfreezeRequestSummaryInput } from 'interface/attendance';

const CALENDAR_DATA_SIZE = 40;

export default function WorkStatusColumn() {
  const lastHandledSummaryErrorAtRef = useRef(0);
  const user = useCurrentUser() as DashboardUser;

  const shouldShowWorkshift =
    INCLUDED_COUNTRIES_FOR_WORKSHIFT.includes(user?.country) &&
    !EXCLUDED_DESIGNATION_FOR_WORKSHIFT.includes(user?.designation?.name);

  const currentFiscalYear = useCurrentFiscalYear() as FiscalYear;

  const {
    selfAttendance: { missing: selfMissing, pending: selfPending },
    setSelfAttendance,
  } = useSelfAttendance();

  const getCalendarMedianTime = (calendarData: CalendarDay[]) => {
    const times = calendarData
      .map(({ checkinTime }) => checkinTime)
      .filter((checkinTime): checkinTime is string => Boolean(checkinTime));

    return getMedianTime(times);
  };

  const endDate = getFormattedDate();
  const startDate = getFormattedDate(currentFiscalYear.startDate);
  const shouldFetchSummary = !isObjectEmpty(currentFiscalYear);

  const selfSummaryFilter = useMemo(
    () => ({
      fetchType: SELF,
      startDate,
      endDate,
      fields: 'worklog,missing',
    }),
    [endDate, startDate]
  );

  const {
    data: selfSummaryResponse,
    isLoading: isSelfAttendanceLoading,
    isError: isSelfSummaryError,
    error: selfSummaryError,
    errorUpdatedAt: selfSummaryErrorUpdatedAt,
  } = useFetchAttendanceSummary(selfSummaryFilter, shouldFetchSummary);

  useEffect(() => {
    if (!isSelfSummaryError || !selfSummaryError || selfSummaryErrorUpdatedAt <= lastHandledSummaryErrorAtRef.current) {
      return;
    }

    handleError(selfSummaryError);
    lastHandledSummaryErrorAtRef.current = selfSummaryErrorUpdatedAt;
  }, [isSelfSummaryError, selfSummaryError, selfSummaryErrorUpdatedAt]);

  const currentDate = getFormattedDate();
  const [weekStartDate, weekEndDate] = getCurrentWeekRange(currentDate);
  const medianStartDate = subtractDays(currentDate, N_OF_MEDIAN_TIME);

  const calendarFilter = useMemo(
    () => ({
      fetchType: SELF,
      startDate: medianStartDate,
      endDate: weekEndDate,
      withCheckinTime: true,
      size: CALENDAR_DATA_SIZE,
    }),
    [medianStartDate, weekEndDate]
  );

  const {
    data: calendarData,
    isLoading: isCalendarDataLoading,
    isPending: isCalendarDataPending,
  } = useFetchAttendanceCalendarQuery<CalendarDay[]>(calendarFilter, Boolean(user?.id));

  const { data: unfreezeRequests = [] } = useFetchUnfreezeRequestsQuery<UnfreezeRequest[]>(user?.id, Boolean(user?.id));

  useEffect(() => {
    if (!selfSummaryResponse?.data) {
      return;
    }

    const selfData = selfSummaryResponse.data as { missing?: number; worklog?: { pending?: number } };
    setSelfAttendance({ missing: selfData.missing || 0, pending: selfData.worklog?.pending || 0 });
  }, [selfSummaryResponse, setSelfAttendance]);

  const { clockinHistory, totalDuration, medianTime, workStatusSummaryData } = useMemo(() => {
    const emptySummary: WorkStatusSummary = { wfh: 0, wfo: 0, leave: 0 };

    if (!calendarData) {
      return {
        clockinHistory: [] as CalendarDay[],
        totalDuration: 0 as string | number,
        medianTime: null as string | null,
        workStatusSummaryData: emptySummary,
      };
    }

    const weeklyData = filterDataByDateRange({
      data: calendarData,
      key: 'date',
      startDate: weekStartDate,
      endDate: weekEndDate,
    }) as CalendarDay[];
    const medianData = filterDataByDateRange({
      data: calendarData,
      key: 'date',
      startDate: medianStartDate,
      endDate: currentDate,
    }) as CalendarDay[];

    const medianTimeValue = getCalendarMedianTime(medianData);
    const unfreezeRequestsSummaryData = unfreezeRequests.filter(
      (request): request is UnfreezeRequestSummaryInput =>
        Boolean(request.unfreezeFrom && request.unfreezeTo && request.updatedAt) &&
        typeof request.isUnfrozen === 'boolean'
    );

    return {
      clockinHistory: medianData,
      totalDuration: getWorklogTotalDuration(weeklyData) as string | number,
      medianTime: medianTimeValue ? convertTimeToAMPM(medianTimeValue) : null,
      workStatusSummaryData: getWorkStatusSummaryData(medianData, unfreezeRequestsSummaryData),
    };
  }, [calendarData, unfreezeRequests, weekStartDate, weekEndDate, medianStartDate, currentDate]);

  const isLoading = isCalendarDataPending || isSelfAttendanceLoading;

  return (
    <div className="work-status-column">
      <WorkStatusCard
        isLoading={isLoading}
        shouldShowWorkshift={shouldShowWorkshift}
        medianTime={medianTime}
        clockinHistory={clockinHistory}
        isClockinHistoryLoading={isCalendarDataLoading}
        unfreezeRequests={unfreezeRequests}
        summary={workStatusSummaryData}
      />

      <LoggingStatusCard totalDuration={totalDuration} selfPending={selfPending} selfMissing={selfMissing} />
    </div>
  );
}
